// /topic/governance 구독 — 화면 12 / 13 / 16 공통 신호.
// 권위: ARCH_DASHBOARD.md v1.8 §4.12 / §6.3 (ADR-DASH-04).
//
// 수신 이벤트:
//   GOVERNANCE.CONTEXT.PUBLISHED          (INFO)
//   GOVERNANCE.CONTEXT.PUBLISH_DEGRADED   (WARN)
//   GOVERNANCE.CONTEXT.PUBLISH_FAILED     (ERROR)
//   GOVERNANCE.AGENT.DOWN                 (FATAL)
//   GOVERNANCE.MANUAL.OVERRIDE_SET        (INFO)
//   GOVERNANCE.MANUAL.OVERRIDE_CLEARED    (INFO)
//
// payload 구조는 §4.1 SystemEvent — event_code + event_id + occurred_at + payload.

import { useEffect } from 'react';
import type { IMessage } from '@stomp/stompjs';
import { subscribe } from './useWebSocket';

export interface GovernanceLiveEvent {
  event_code: string;
  event_id?: string;
  occurred_at?: string;
  severity?: string;
  trace_id?: string;
  payload?: Record<string, unknown>;
}

function normalize(raw: unknown): GovernanceLiveEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const code = (obj.event_code ?? obj.eventCode) as string | undefined;
  if (!code || typeof code !== 'string') return null;
  return {
    event_code: code,
    event_id: (obj.event_id ?? obj.eventId) as string | undefined,
    occurred_at: (obj.occurred_at ?? obj.occurredAt) as string | undefined,
    severity: obj.severity as string | undefined,
    trace_id: (obj.trace_id ?? obj.traceId) as string | undefined,
    payload: (obj.payload ?? undefined) as Record<string, unknown> | undefined,
  };
}

/**
 * /topic/governance 구독. event_code prefix 로 거버넌스 관련 이벤트만 필터한다.
 */
export function useGovernanceLive(
  handler: (event: GovernanceLiveEvent) => void,
): void {
  useEffect(() => {
    const unsub = subscribe('/topic/governance', (msg: IMessage) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(msg.body);
      } catch {
        return;
      }
      const ev = normalize(parsed);
      if (!ev) return;
      if (!ev.event_code.startsWith('GOVERNANCE.')) return;
      try {
        handler(ev);
      } catch {
        /* 핸들러 예외는 무시 — 구독 자체는 유지. */
      }
    });
    return unsub;
  }, [handler]);
}
